import type { CellRange, Sheet } from './types'
import { DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT } from './types'

export type PaperName = 'A4' | 'A3' | 'B5' | 'Letter' | 'Legal'
export type Orientation = 'portrait' | 'landscape'
export type MarginName = 'narrow' | 'normal' | 'wide'
export type FitMode = 'none' | 'width' | 'page'
export type Scope = 'sheet' | 'selection' | 'workbook'

export interface PdfOptions {
  scope: Scope
  paper: PaperName
  orientation: Orientation
  margin: MarginName
  fit: FitMode
  /** fit === 'none' 일 때의 배율(%) */
  scale: number
  gridlines: boolean
  /** A·B·C / 1·2·3 머리글 인쇄 */
  rowColHeaders: boolean
  /** 페이지마다 반복할 상단 행 수 */
  repeatRows: number
  /** 시트 이름을 제목으로 */
  showTitle: boolean
}

export const DEFAULT_PDF_OPTIONS: PdfOptions = {
  scope: 'sheet',
  paper: 'A4',
  orientation: 'landscape',
  margin: 'normal',
  fit: 'width',
  scale: 100,
  gridlines: true,
  rowColHeaders: false,
  repeatRows: 1,
  showTitle: true,
}

/** mm 단위 용지 크기 (세로 기준) */
export const PAPER_MM: Record<PaperName, [number, number]> = {
  A4: [210, 297],
  A3: [297, 420],
  B5: [176, 250],
  Letter: [215.9, 279.4],
  Legal: [215.9, 355.6],
}

export const MARGIN_MM: Record<MarginName, number> = { narrow: 8, normal: 15, wide: 25 }

const PX_PER_MM = 96 / 25.4

export interface PageMetrics {
  /** @page size 에 넣을 문자열. 예: "210mm 297mm" */
  sizeCss: string
  marginMm: number
  /** 여백을 뺀 인쇄 가능 영역 (px, 96dpi 기준) */
  availWpx: number
  availHpx: number
}

export function pageMetrics(o: PdfOptions): PageMetrics {
  const [pw, ph] = PAPER_MM[o.paper]
  const [w, h] = o.orientation === 'landscape' ? [ph, pw] : [pw, ph]
  const m = MARGIN_MM[o.margin]
  return {
    sizeCss: `${w}mm ${h}mm`,
    marginMm: m,
    availWpx: (w - m * 2) * PX_PER_MM,
    availHpx: (h - m * 2) * PX_PER_MM,
  }
}

export function colW(sheet: Sheet, c: number) {
  return sheet.colWidths[c] ?? DEFAULT_COL_WIDTH
}
export function rowH(sheet: Sheet, r: number) {
  return sheet.rowHeights[r] ?? DEFAULT_ROW_HEIGHT
}

export function rangeWidthPx(sheet: Sheet, r: CellRange, withRowHeader: boolean) {
  let w = withRowHeader ? 44 : 0
  for (let c = r.c1; c <= r.c2; c++) w += colW(sheet, c)
  return w
}

export function rangeHeightPx(sheet: Sheet, r: CellRange, withColHeader: boolean) {
  let h = withColHeader ? 22 : 0
  for (let row = r.r1; row <= r.r2; row++) h += rowH(sheet, row)
  return h
}

/** 옵션과 실제 표 크기로부터 최종 배율(zoom)을 구한다. */
export function zoomFor(o: PdfOptions, m: PageMetrics, tableWpx: number, tableHpx: number): number {
  if (o.fit === 'none') return Math.max(0.1, Math.min(4, o.scale / 100))
  const kw = m.availWpx / Math.max(1, tableWpx)
  if (o.fit === 'width') return Math.min(1, kw)
  return Math.min(1, kw, m.availHpx / Math.max(1, tableHpx))
}

/** 세로로 몇 장이 나올지 대략 계산 (가로는 fit=width 면 1장) */
export function estimatePages(o: PdfOptions, m: PageMetrics, tableWpx: number, tableHpx: number) {
  const z = zoomFor(o, m, tableWpx, tableHpx)
  const down = Math.max(1, Math.ceil((tableHpx * z) / m.availHpx))
  const across = Math.max(1, Math.ceil((tableWpx * z) / m.availWpx))
  return { zoom: z, down, across, total: down * across }
}

export const PAPER_LABELS: Record<PaperName, string> = {
  A4: 'A4 (210×297)',
  A3: 'A3 (297×420)',
  B5: 'B5 (176×250)',
  Letter: 'Letter (8.5×11")',
  Legal: 'Legal (8.5×14")',
}
